<script lang="ts">
    import { onMount } from 'svelte';
    import Button from '../../../Button.svelte';
    import Table from '../../../Table.svelte';
    import Pagination from '../../../Pagination.svelte';
    import Dropdown from '../../../Dropdown.svelte';
    import DateRange from '../../../DateRange.svelte';
    import SearchInput from '../../../SearchInput.svelte';
    import PaymentReportService, { type PaymentReportPayout, type PaymentReportFilters, type EmployerStats } from '../../../../lib/api/employer/payment-report';
    import { push } from 'svelte-spa-router';
    import toast from 'svelte-french-toast';
    import { paymentStatusOptions } from '../../../../lib/utils/genericStatusOptions';
  
    // State
    let isLoading = true;
    let error = '';
    let invoices: any[] = [];
    let totalPages = 1;
    let currentPage = 1;
    let totalItems = 0;
    let itemsPerPage = 20;
  
    // Metrics
    let stats: EmployerStats = {
      total_donations: 0,
      total_hours: 0,
      total_value: 0,
      active_employees: 0,
      pending_requests: 0,
      approved_requests: 0,
      rejected_requests: 0
    };
  
    // Filters
    let startDate = '';
    let endDate = '';
    let selectedStatus = '';
    let searchPhrase = '';
    let sortBy = 'transaction_date';
    let sortDirection: 'asc' | 'desc' = 'desc';
  
        const tableColumns = [
      { 
        key: 'invoice_id', 
        label: 'Invoice ID', 
        sortable: true,
        render: (value: any, row: any) => row.invoice_id || 'N/A'
      },
      { 
        key: 'invoice_date', 
        label: 'Date', 
        sortable: true,
        render: (value: any, row: any) => {
          if (row.invoice_date) {
            try {
              const date = new Date(row.invoice_date);
              const day = date.getDate().toString().padStart(2, '0');
              const month = date.toLocaleDateString('en-US', { month: 'short' });
              const year = date.getFullYear();
              return `${day}-${month}-${year}`;
            } catch {
              return 'N/A';
            }
          }
          return 'N/A';
        }
      },
      { 
        key: 'total_value', 
        label: 'Payment Received', 
        sortable: true,
        render: (value: any, row: any) => `$${parseFloat(row.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      },
      { 
        key: 'total_contribution', 
        label: 'Our Contribution', 
        sortable: true,
        render: (value: any, row: any) => `$${parseFloat(row.total_contribution || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      },
      { 
        key: 'total_contribution_display', 
        label: 'Total Contribution', 
        sortable: true,
        render: (value: any, row: any) => {
          // Total Contribution = Payment Received (employee + employer) = total_value
          const total = parseFloat(row.total_value || 0);
          return `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
      },
      { 
        key: 'total_l2g_fee', 
        label: 'Transaction Fee',
        sortable: true,
        render: (value: any, row: any) => `$${parseFloat(row.total_l2g_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      },
      { 
        key: 'net_payment', 
        label: 'Net to Charities', 
        sortable: true,
        render: (value: any, row: any) => `$${parseFloat(row.net_payment || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      },
      { 
        key: 'payments_completed', 
        label: 'Payments', 
        sortable: false,
        render: (value: any, row: any) => {
          const completed = row.payments_completed || 0;
          const total = row.total_expected_payments || completed;
          const isFullyCompleted = completed === total;
          return total > 0 ? `<span class="payments-fraction ${isFullyCompleted ? 'fully-completed-text' : 'partial-text'}">${completed}/${total}</span>` : '<span class="payments-fraction">—</span>';
        }
      },
      { 
        key: 'completion_status',
        label: 'Status',
        align: 'center',
        sortable: false,
        render: (value: any, row: any) => {
          const completed = row.payments_completed || 0;
          const total = row.total_expected_payments || completed;
          const hasReferences = row.has_payment_references || false;
          
          // If all charities have payment references, it's fully donated
          // If no references exist, it's still with L2G
          if (hasReferences && completed === total && completed > 0) {
            return `<div class="completion-status-badge fully-completed">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>Fully Donated</span>
            </div>`;
          } else if (hasReferences && completed > 0) {
            const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
            return `<div class="completion-status-badge partially-completed">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
                <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
              </svg>
              <span>Distributing (${percentage}%)</span>
            </div>`;
          } else {
            return `<div class="completion-status-badge with-l2g">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M17 13H11V7H12.5V11.5H17V13Z"/>
              </svg>
              <span>With L2G</span>
            </div>`;
          }
        }
      },
    ];
  
    // Reactive statement to ensure invoices is always an array
    $: if (!Array.isArray(invoices)) {
      invoices = [];
    }
  
    onMount(() => {
      loadInvoices();
      loadStats();
    });

    function handleDateRangeApply() {
      currentPage = 1;
      loadInvoices();
      loadStats();
    }
  
    async function loadInvoices() {
      try {
        isLoading = true;
        error = '';
        
        const filters: PaymentReportFilters = {
          page: Math.max(currentPage, 1), // API expects 1-based pagination
          per_page: itemsPerPage
        };
        
        // Add filters if they exist
        if (searchPhrase) filters.phrase = searchPhrase;
        if (startDate) filters.start_date = startDate;
        if (endDate) filters.end_date = endDate;
        if (sortBy) filters.sort_by = sortBy as any;
        if (sortDirection) filters.sort_direction = sortDirection;
        
        const response = await PaymentReportService.getPaidInvoices(filters);
        
        if (response.success && (response.items || response.data)) {
          invoices = response.items || response.data || [];
          totalItems = response.paging?.total || 0;
          totalPages = response.paging?.last_page || 1;
          // API returns current_page as 0-based but expects requests with 1-based page numbers
          currentPage = Math.max((response.paging?.current_page || 0) + 1, 1);
          
          console.log('Loaded invoices:', invoices);
        } else {
          error = response.message || 'Failed to load invoices';
          toast.error(error);
          invoices = [];
        }
      } catch (err) {
        error = 'Failed to load invoices';
        toast.error(error);
        console.error('Error loading invoices:', err);
        invoices = [];
        totalItems = 0;
        totalPages = 1;
      } finally {
        isLoading = false;
      }
    }
    
    async function loadStats() {
      try {
        const filters: PaymentReportFilters = {};
        if (startDate) filters.start_date = startDate;
        if (endDate) filters.end_date = endDate;

        const response = await PaymentReportService.getCompletedPaymentStats(filters);
        if (response.success && response.data) {
          stats = response.data;
        }
      } catch (err) {
        console.error('Error loading stats:', err);
        // Keep default stats values in case of error
      }
    }
    

  
    function handleSearch() {
      currentPage = 1;
      loadInvoices();
    }
  
    function handlePageChange(event: CustomEvent<{ page: number }>) {
      currentPage = event.detail.page;
      loadInvoices();
    }
    
    function handleSort(field: string) {
      if (sortBy === field) {
        sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        sortBy = field;
        sortDirection = 'desc';
      }
      currentPage = 1;
      loadInvoices();
    }
  
    function handleRowClick(row: any) {
      // Navigate to paid invoice detail view
      if (row && row.invoice_id) {
        push(`/employer/payment-history/${row.invoice_id}`);
      }
    }
  </script>
  
  <div class="payment-report-listing">
    <!-- Top Metrics Cards -->
    <p class="metrics-description">Summary totals for completed payments below.</p>
    <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-content">
          <div class="metric-left">
            <div class="metric-label-container">
              <span class="metric-dot" style="background-color: #FCB201;"></span>
              <span class="metric-label">Donations Paid</span>
            </div>
            <div class="metric-value">{stats.total_donations}</div>
          </div>
          <div class="metric-icon bg-yellow">
            <svg class="icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 15.7486C21 19.8873 17.2499 21.7477 12 21.7477C6.75012 21.7477 3 19.8873 3 15.7779C3 11.2786 5.25035 8.99899 9.74965 6.7194L8.15658 3.7519C8.06153 3.60326 8.0086 3.43162 8.00344 3.25527C7.99828 3.07891 8.04107 2.90447 8.12726 2.75052C8.21345 2.59658 8.3398 2.46892 8.49285 2.38115C8.64589 2.29337 8.81989 2.24878 8.99629 2.25213H15.356C15.5214 2.25636 15.6829 2.30259 15.8255 2.38647C15.968 2.47035 16.0869 2.58912 16.1708 2.7316C16.2548 2.87409 16.3012 3.0356 16.3055 3.20093C16.3099 3.36627 16.2721 3.52999 16.1957 3.6767L14.2503 6.7194C18.7483 8.96836 21 11.248 21 15.7486ZM12.8703 9.05748C12.8703 8.82665 12.7786 8.60528 12.6154 8.44206C12.4522 8.27884 12.2308 8.18714 12 8.18714C11.7692 8.18714 11.5478 8.27884 11.3846 8.44206C11.2214 8.60528 11.1297 8.82665 11.1297 9.05748V9.93061C10.5398 9.95333 9.97844 10.1906 9.55113 10.598C9.12382 11.0053 8.85991 11.5546 8.80896 12.1427C8.75801 12.7309 8.92353 13.3174 9.27443 13.7921C9.62533 14.2668 10.1375 14.5971 10.7147 14.721L12.7659 15.1693C12.9761 15.2146 13.1622 15.3361 13.2881 15.5104C13.4141 15.6847 13.4711 15.8995 13.4481 16.1133C13.4251 16.3271 13.3237 16.5249 13.1635 16.6684C13.0034 16.8119 12.7957 16.891 12.5807 16.8905H11.4193C11.2393 16.8908 11.0636 16.8353 10.9164 16.7315C10.7693 16.6277 10.6579 16.4809 10.5977 16.3112C10.5625 16.1999 10.5053 16.0968 10.4295 16.008C10.3537 15.9192 10.2608 15.8466 10.1563 15.7945C10.0518 15.7424 9.9379 15.7118 9.82137 15.7047C9.70484 15.6975 9.58805 15.7138 9.47796 15.7527C9.36787 15.7915 9.26673 15.8522 9.18056 15.931C9.09438 16.0097 9.02494 16.105 8.97636 16.2112C8.92778 16.3174 8.90105 16.4322 8.89778 16.5489C8.8945 16.6656 8.91474 16.7818 8.95729 16.8905C9.12141 17.3527 9.41207 17.7596 9.79616 18.0646C10.1802 18.3697 10.6423 18.5607 11.1297 18.6159V19.5015C11.1297 19.7324 11.2214 19.9537 11.3846 20.117C11.5478 20.2802 11.7692 20.3719 12 20.3719C12.2308 20.3719 12.4522 20.2802 12.6154 20.117C12.7786 19.9537 12.8703 19.7324 12.8703 19.5015V18.6159C13.4848 18.546 14.0546 18.2605 14.4783 17.81C14.9019 17.3595 15.1521 16.7733 15.1842 16.1557C15.2162 15.5381 15.0282 14.9292 14.6535 14.4372C14.2787 13.9453 13.7416 13.6022 13.1377 13.4691L11.0865 13.0207C10.9201 12.9866 10.7723 12.8917 10.6722 12.7545C10.572 12.6173 10.5267 12.4476 10.5449 12.2787C10.5632 12.1099 10.6438 11.9539 10.771 11.8413C10.8982 11.7287 11.0629 11.6676 11.2327 11.6699H12.5807C12.761 11.6686 12.9372 11.7237 13.0845 11.8276C13.2318 11.9315 13.343 12.0789 13.4023 12.2492C13.4833 12.4614 13.644 12.6336 13.8502 12.7289C14.0564 12.8242 14.2917 12.8351 14.5059 12.7593C14.72 12.6835 14.896 12.527 14.9963 12.3232C15.0966 12.1194 15.1133 11.8844 15.0427 11.6685C14.8798 11.2057 14.5895 10.7983 14.2051 10.4933C13.8208 10.1883 13.3581 9.99804 12.8703 9.94453V9.05748Z"/>
            </svg>
          </div>
        </div>
      </div>
  
      <div class="metric-card">
        <div class="metric-content">
          <div class="metric-left">
            <div class="metric-label-container">
              <span class="metric-dot" style="background-color: #2A9D8F;"></span>
              <span class="metric-label">Total Hours</span>
            </div>
            <div class="metric-value">{stats.total_hours}</div>
          </div>
          <div class="metric-icon bg-teal">
            <svg class="icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M17 13H11V7H12.5V11.5H17V13Z"/>
            </svg>
          </div>
        </div>
      </div>
  
      <div class="metric-card">
        <div class="metric-content">
          <div class="metric-left">
            <div class="metric-label-container">
              <span class="metric-dot" style="background-color: #007AFF;"></span>
              <span class="metric-label">Total Paid</span>
            </div>
            <div class="metric-value">${stats.total_value}</div>
          </div>
          <div class="metric-icon bg-blue">
            <svg class="icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 15H9C9 16.08 10.37 17 12 17S15 16.08 15 15C15 13.9 13.96 13.5 11.76 12.97C9.64 12.44 7 11.78 7 9C7 7.21 8.47 5.69 10.5 5.18V3H13.5V5.18C15.53 5.69 17 7.21 17 9H15C15 7.92 13.63 7 12 7S9 7.92 9 9C9 10.1 10.04 10.5 12.24 11.03C14.36 11.56 17 12.22 17 15C17 16.79 15.53 18.31 13.5 18.82V21H10.5V18.82C8.47 18.31 7 16.79 7 15Z"/>
            </svg>
          </div>
        </div>
      </div>
  
      <div class="metric-card">
        <div class="metric-content">
          <div class="metric-left">
            <div class="metric-label-container">
              <span class="metric-dot" style="background-color: #37B0FC;"></span>
              <span class="metric-label">Active Employees</span>
            </div>
            <div class="metric-value">{stats.active_employees}</div>
          </div>
          <div class="metric-icon bg-blue-employee">
            <svg class="icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 4C18.2 4 20 5.8 20 8S18.2 12 16 12 12 10.2 12 8 13.8 4 16 4M16 14C20.4 14 24 15.8 24 18V20H8V18C8 15.8 11.6 14 16 14M8.5 4C10.7 4 12.5 5.8 12.5 8S10.7 12 8.5 12 4.5 10.2 4.5 8 6.3 4 8.5 4M8.5 14C12.9 14 16.5 15.8 16.5 18V20H0V18C0 15.8 4.1 14 8.5 14Z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  
    <!-- Filters Section -->
    <div class="filters-section">
      <div class="filters-left">
        <SearchInput
          bind:value={searchPhrase}
          placeholder="Search payments..."
          on:search={handleSearch}
          width="250px"
        />
        
        <DateRange bind:startDate bind:endDate on:apply={handleDateRangeApply} />
      </div>
    </div>
  
    <!-- Payments List Section -->
    <div class="payments-section">
      <h2 class="section-title">Completed Payments</h2>
      <p class="page-description">
        Completed payment batches grouped by payout date. Totals show only your organisation's share when a batch includes multiple employers.
      </p>
      
      {#if error}
        <div class="error-message">
          <p>{error}</p>
          <Button variant="secondary" on:click={loadInvoices}>Try Again</Button>
        </div>
      {:else}
        {#if isLoading}
          <div class="loading-message">Loading payment history...</div>
        {:else if !invoices || invoices.length === 0}
          <div class="empty-message">No payment history found</div>
        {:else}
          <Table
            columns={tableColumns}
            data={invoices}
            onRowClick={handleRowClick}
          />
        {/if}
  
        {#if totalPages > 1}
          <div class="pagination-wrapper">
            <Pagination
              {currentPage}
              {totalPages}
              on:pageChange={handlePageChange}
            />
          </div>
        {/if}
      {/if}
    </div>
  </div>
  
  <style>
    .payment-report-listing {
      padding: 0;
      max-width: 100%;
    }

    .metrics-description {
      font-size: 0.875rem;
      color: #6b7280;
      margin: 0 0 1rem 0;
      line-height: 1.5;
    }
  
    /* Metrics Grid */
    .metrics-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1.5rem;
      margin-bottom: 2rem;
    }
  
    .metric-card {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
      border: 1px solid #f3f4f6;
      transition: transform 0.2s, box-shadow 0.2s;
    }
  
    .metric-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }
  
    .metric-content {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      height: 100%;
    }
  
    .metric-left {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  
    .metric-label-container {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  
    .metric-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }
  
    .metric-label {
      font-size: 0.875rem;
      font-weight: 500;
      color: #6b7280;
    }
  
    .metric-value {
      font-size: 1.5rem;
      font-weight: 500;
      color: #111827;
      line-height: 1;
    }
  
    .metric-icon {
      width: 3rem;
      height: 3rem;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
  
    .icon {
      width: 1.5rem;
      height: 1.5rem;
      color: white;
    }
  
  
    .bg-yellow { background-color: #FCB201; }
    .bg-teal { background-color: #2A9D8F; }
    .bg-blue { background-color: #007AFF; }

    .bg-blue-employee { background-color: #37B0FC; }
  
  
    /* Filters Section */
    .filters-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 2rem;
      background: white;
      padding: 1.5rem;
      border-radius: 12px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      width: 100%;
      margin-bottom: 1.5rem;
    }

    .filters-left {
      display: flex;
      gap: 1rem;
      align-items: center;
      flex: 1;
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .filters-section {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
      }

      .filters-left {
        flex-direction: column;
        gap: 0.75rem;
      }
    }
  
    /* Payments Section */
    .payments-section {
      background: white;
      border-radius: 12px;
      padding: 1rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      border: 1px solid #f3f4f6;
    }
  
    .section-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
      margin: 0 0 0.5rem 0;
    }

    .page-description {
      font-size: 0.875rem;
      color: #6b7280;
      margin: 0 0 1rem 0;
      line-height: 1.5;
      max-width: 800px;
    }
  
    .error-message {
      text-align: center;
      padding: 2rem;
      color: #ef4444;
    }
  
    .error-message p {
      margin-bottom: 1rem;
      font-size: 1rem;
    }
  
    .loading-message,
    .empty-message {
      text-align: center;
      padding: 2rem;
      color: #6b7280;
      font-size: 1rem;
    }
  
    .pagination-wrapper {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid #e5e7eb;
    }
  
    
  
    /* Status badges - More specific targeting */
    :global(.status-badge) {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 0.375rem 0.75rem !important;
      border-radius: 9999px !important;
      font-size: 0.875rem !important;
      font-weight: 500 !important;
      line-height: 1 !important;
      white-space: nowrap !important;
    }
  
    :global(.status-badge svg) {
      width: 16px !important;
      height: 16px !important;
      margin-right: 0.25rem !important;
      flex-shrink: 0 !important;
      display: block !important;
    }
  
    :global(.status-badge span) {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  
    :global(.status-badge.pending) {
      background-color: #FFF8E1 !important;
      color: #F57C00 !important;
    }
  
    :global(.status-badge.completed) {
      background-color: #E6F4EA !important;
      color: #34A853 !important;
    }
  
    :global(.status-badge.rejected) {
      background-color: #FDEDEB !important;
      color: #EA4335 !important;
    }
  
    :global(.view-btn-payment-report-employer) {
      background: #111827 !important;
      color: white !important;
      border: none !important;
      border-radius: 50px !important;
      padding: 0.5rem 1rem !important;
      font-size: 0.875rem !important;
      font-weight: 500 !important;
      cursor: pointer !important;
      transition: background-color 0.2s !important;
    }

    :global(.primary) {
      color: black !important;
      font-weight: 500 !important;
    }
  
    :global(.view-btn-payment-report-employer:hover) {
      background: #1f2937 !important;
    }
  
    :global(.status-badge::before) {
      display: none !important;  
    }
  
    /* Responsive design */
    @media (max-width: 1024px) {
      .metrics-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
      }
    }
  
    @media (max-width: 768px) {
      .payment-report-listing {
        padding: 1rem;
      }
  
    .metrics-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }

  /* Completion status badges */
  :global(.completion-status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.completion-status-badge svg) {
    flex-shrink: 0;
  }

  /* Distribution statuses, coloured from the canonical palette (TONE_PALETTE in
     src/lib/donationLifecycle.ts): With L2G = cyan (employer-paid), Distributing = teal
     (paid-to-charity), Fully Donated = green (received-by-charity). */
  :global(.completion-status-badge.fully-completed) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.completion-status-badge.partially-completed) {
    background-color: #ccfbf1;
    color: #0f766e;
  }

  :global(.completion-status-badge.with-l2g) {
    background-color: #cffafe;
    color: #155e75;
  }

  :global(.payments-fraction) {
    font-weight: 600;
    font-family: monospace;
  }

  :global(.payments-fraction.fully-completed-text) {
    color: #065f46;
  }

  :global(.payments-fraction.partial-text) {
    color: #0f766e;
  }
  </style>